import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import Navbar from '../components/layout/Navbar';
import LoadingScreen from '../components/layout/LoadingScreen';
import { trackPageView } from '../services/tracking';
import { trackPageView as trackFunnelPageView } from '../utils/funnel';


// Auth screens
import LoginScreen from '../screens/Auth/LoginScreen';

// Public screens
import HomeScreen from '../screens/Home/HomeScreen';
import CourseDetailScreen from '../screens/Home/CourseDetailScreen';
import CourseLandingPage from '../screens/Home/CourseLandingPage';
import CoursesListScreen from '../screens/Home/CoursesListScreen';
import WorkshopsListScreen from '../screens/Home/WorkshopsListScreen';
import WorkshopDetailScreen from '../screens/Home/WorkshopDetailScreen';
import AboutScreen from '../screens/Static/AboutScreen';
import ContactScreen from '../screens/Static/ContactScreen';
import BlogListScreen from '../screens/Blog/BlogListScreen';
import BlogDetailScreen from '../screens/Blog/BlogDetailScreen';
import NewsListScreen from '../screens/News/NewsListScreen';
import NewsDetailScreen from '../screens/News/NewsDetailScreen';

// Legal screens
import TermsScreen from '../screens/Legal/TermsScreen';
import PrivacyScreen from '../screens/Legal/PrivacyScreen';
import RefundPolicyScreen from '../screens/Legal/RefundPolicyScreen';

// Protected screens
import EnrollmentFormScreen from '../screens/Enrollment/EnrollmentFormScreen';
import PaymentScreen from '../screens/Enrollment/PaymentScreen';
import DashboardScreen from '../screens/Dashboard/DashboardScreen';
import MyCoursesScreen from '../screens/Dashboard/MyCoursesScreen';
import ProfileScreen from '../screens/Dashboard/ProfileScreen';
import NotificationsScreen from '../screens/Dashboard/NotificationsScreen';
import CertificateScreen from '../screens/Dashboard/CertificateScreen';

// Admin screens
import AdminDashboard from '../screens/Admin/AdminDashboard';
import AdminCourses from '../screens/Admin/AdminCourses';
import AdminWorkshops from '../screens/Admin/AdminWorkshops';
import AdminBlogs from '../screens/Admin/AdminBlogs';
import AdminWorkshopDetail from '../screens/Admin/AdminWorkshopDetail';
import AdminCertificates from '../screens/Admin/AdminCertificates';
import AdminUsers from '../screens/Admin/AdminUsers';
import AdminEnrollments from '../screens/Admin/AdminEnrollments';
import AdminPayments from '../screens/Admin/AdminPayments';
import AdminLeads from '../screens/Admin/AdminLeads';
import AdminSettings from '../screens/Admin/AdminSettings';
import AdminModeration from '../screens/Admin/AdminModeration';
import AdminCMS from '../screens/Admin/AdminCMS';
import AdminLogs from '../screens/Admin/AdminLogs';
import AdminSupportTickets from '../screens/Admin/AdminSupportTickets';
import AdminPayouts from '../screens/Admin/AdminPayouts';
import AdminMarketing from '../screens/Admin/AdminMarketing';
import AdminAbandonedCarts from '../screens/Admin/AdminAbandonedCarts';
import AdminReferrals from '../screens/Admin/AdminReferrals';
import AdminReferralEarnings from '../screens/Admin/AdminReferralEarnings';
import AdminReferralPayouts from '../screens/Admin/AdminReferralPayouts';
import AdminReferralApplications from '../screens/Admin/AdminReferralApplications';
import CourseModules from '../screens/Admin/CourseModules';
import CourseFaqManager from '../screens/Admin/CourseFaqManager';
import WhatsAppManager from '../screens/Admin/WhatsAppManager';
import EmailManager from '../screens/Admin/EmailManager';

// Trainer screens
import TrainerDashboard from '../screens/Trainer/TrainerDashboard';
import TrainerCourses from '../screens/Trainer/TrainerCourses';
import TrainerStudents from '../screens/Trainer/TrainerStudents';
import TrainerMaterials from '../screens/Trainer/TrainerMaterials';
import TrainerAnnouncements from '../screens/Trainer/TrainerAnnouncements';
import TrainerEarnings from '../screens/Trainer/TrainerEarnings';
import TrainerApply from '../screens/Trainer/TrainerApply';

// New user screens
import WishlistScreen from '../screens/Dashboard/WishlistScreen';
import MyNotesScreen from '../screens/Dashboard/MyNotesScreen';
import MessagesScreen from '../screens/Dashboard/MessagesScreen';
import MentorChatScreen from '../screens/Mentor/MentorChatScreen';
import ReferralDashboard from '../screens/Referral/ReferralDashboard';

// Dev / Integration Test
import IntegrationTestScreen from '../screens/Dev/IntegrationTestScreen';

function AuthGuard({ children, allowedRoles = ['user', 'trainer', 'admin'] }: { children: React.ReactNode; allowedRoles?: string[] }): React.JSX.Element {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { hasAnyRole } = usePermissions();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen message="Authenticating..." />;
  }
  if (!isAuthenticated) {
    // Save intended destination so we can redirect back after login
    localStorage.setItem('redirect_after_login', location.pathname + location.search + location.hash);
    return <Navigate to="/login" replace />;
  }
  if (!hasAnyRole(allowedRoles)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function AnimatedRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const location = useLocation();
  return (
    <motion.div
      key={location.pathname}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
    >
      {children}
    </motion.div>
  );
}

export default function AppNavigator(): React.JSX.Element {
  const location = useLocation();
  const { isAdmin, isTrainer } = usePermissions();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
    trackFunnelPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Show navbar everywhere except login, home (has its own), and admin/trainer pages
  const isAdminPage = location.pathname.startsWith('/admin');
  const isTrainerPage = location.pathname.startsWith('/trainer');
  const isHomePage = location.pathname === '/';
  const showNavbar = location.pathname !== '/login' && !isHomePage && !isAdminPage && !isTrainerPage;
  const showLeadPopup = false;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50/50">
      {showNavbar && <Navbar />}

      <main className="flex-1">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            {/* Auth */}
            <Route path="/login" element={<LoginScreen />} />

            {/* Public routes */}
            <Route path="/" element={<AnimatedRoute><HomeScreen /></AnimatedRoute>} />
            <Route path="/courses" element={<AnimatedRoute><CoursesListScreen /></AnimatedRoute>} />
            <Route path="/courses/:slug" element={<AnimatedRoute><CourseDetailScreen /></AnimatedRoute>} />
            <Route path="/workshops" element={<AnimatedRoute><WorkshopsListScreen /></AnimatedRoute>} />
            <Route path="/workshops/:id" element={<AnimatedRoute><WorkshopDetailScreen /></AnimatedRoute>} />
            <Route path="/l/:slug" element={<AnimatedRoute><CourseLandingPage /></AnimatedRoute>} />
            <Route path="/about" element={<AnimatedRoute><AboutScreen /></AnimatedRoute>} />
            <Route path="/contact" element={<AnimatedRoute><ContactScreen /></AnimatedRoute>} />
            <Route path="/blog" element={<AnimatedRoute><BlogListScreen /></AnimatedRoute>} />
            <Route path="/blog/:slug" element={<AnimatedRoute><BlogDetailScreen /></AnimatedRoute>} />
            <Route path="/news" element={<AnimatedRoute><NewsListScreen /></AnimatedRoute>} />
            <Route path="/news/:id" element={<AnimatedRoute><NewsDetailScreen /></AnimatedRoute>} />
            <Route path="/terms" element={<AnimatedRoute><TermsScreen /></AnimatedRoute>} />
            <Route path="/privacy" element={<AnimatedRoute><PrivacyScreen /></AnimatedRoute>} />
            <Route path="/refund-policy" element={<AnimatedRoute><RefundPolicyScreen /></AnimatedRoute>} />

            {/* Protected user routes */}
            <Route path="/enroll/:slug" element={<AuthGuard><AnimatedRoute><EnrollmentFormScreen /></AnimatedRoute></AuthGuard>} />
            <Route path="/payment" element={<AuthGuard><AnimatedRoute><PaymentScreen /></AnimatedRoute></AuthGuard>} />
            <Route path="/dashboard" element={<AuthGuard><AnimatedRoute><DashboardScreen /></AnimatedRoute></AuthGuard>} />
            <Route path="/my-courses" element={<AuthGuard><AnimatedRoute><MyCoursesScreen /></AnimatedRoute></AuthGuard>} />
            <Route path="/profile" element={<AuthGuard><AnimatedRoute><ProfileScreen /></AnimatedRoute></AuthGuard>} />
            <Route path="/notifications" element={<AuthGuard><AnimatedRoute><NotificationsScreen /></AnimatedRoute></AuthGuard>} />
            <Route path="/certificate/:id" element={<AuthGuard><AnimatedRoute><CertificateScreen /></AnimatedRoute></AuthGuard>} />
            <Route path="/wishlist" element={<AuthGuard><AnimatedRoute><WishlistScreen /></AnimatedRoute></AuthGuard>} />
            <Route path="/notes" element={<AuthGuard><AnimatedRoute><MyNotesScreen /></AnimatedRoute></AuthGuard>} />
            <Route path="/messages" element={<AuthGuard><AnimatedRoute><MessagesScreen /></AnimatedRoute></AuthGuard>} />
            <Route path="/mentor" element={<AuthGuard><AnimatedRoute><MentorChatScreen /></AnimatedRoute></AuthGuard>} />
            <Route path="/referral" element={<AuthGuard><AnimatedRoute><ReferralDashboard /></AnimatedRoute></AuthGuard>} />
            <Route path="/trainer/apply" element={<AuthGuard><AnimatedRoute><TrainerApply /></AnimatedRoute></AuthGuard>} />

            {/* Admin routes */}
            <Route path="/admin" element={<AuthGuard allowedRoles={['admin', 'moderator']}><AdminDashboard /></AuthGuard>} />
            <Route path="/admin/courses" element={<AuthGuard allowedRoles={['admin', 'moderator']}><AdminCourses /></AuthGuard>} />
            <Route path="/admin/workshops" element={<AuthGuard allowedRoles={['admin', 'moderator']}><AdminWorkshops /></AuthGuard>} />
            <Route path="/admin/workshops/:id" element={<AuthGuard allowedRoles={['admin', 'moderator']}><AdminWorkshopDetail /></AuthGuard>} />
            <Route path="/admin/blogs" element={<AuthGuard allowedRoles={['admin', 'moderator']}><AdminBlogs /></AuthGuard>} />
            <Route path="/admin/certificates" element={<AuthGuard allowedRoles={['admin', 'moderator']}><AdminCertificates /></AuthGuard>} />
            <Route path="/admin/users" element={<AuthGuard allowedRoles={['admin']}><AdminUsers /></AuthGuard>} />
            <Route path="/admin/enrollments" element={<AuthGuard allowedRoles={['admin']}><AdminEnrollments /></AuthGuard>} />
            <Route path="/admin/payments" element={<AuthGuard allowedRoles={['admin']}><AdminPayments /></AuthGuard>} />
            <Route path="/admin/leads" element={<AuthGuard allowedRoles={['admin']}><AdminLeads /></AuthGuard>} />
            <Route path="/admin/settings" element={<AuthGuard allowedRoles={['admin']}><AdminSettings /></AuthGuard>} />
            <Route path="/admin/cms" element={<AuthGuard allowedRoles={['admin']}><AdminCMS /></AuthGuard>} />
            <Route path="/admin/moderation" element={<AuthGuard allowedRoles={['admin']}><AdminModeration /></AuthGuard>} />
            <Route path="/admin/support" element={<AuthGuard allowedRoles={['admin']}><AdminSupportTickets /></AuthGuard>} />
            <Route path="/admin/logs" element={<AuthGuard allowedRoles={['admin']}><AdminLogs /></AuthGuard>} />
            <Route path="/admin/payouts" element={<AuthGuard allowedRoles={['admin']}><AdminPayouts /></AuthGuard>} />
            <Route path="/admin/marketing" element={<AuthGuard allowedRoles={['admin']}><AdminMarketing /></AuthGuard>} />
            <Route path="/admin/abandoned-carts" element={<AuthGuard allowedRoles={['admin']}><AdminAbandonedCarts /></AuthGuard>} />
            <Route path="/admin/referrals" element={<AuthGuard allowedRoles={['admin']}><AdminReferrals /></AuthGuard>} />
            <Route path="/admin/referral-earnings" element={<AuthGuard allowedRoles={['admin']}><AdminReferralEarnings /></AuthGuard>} />
            <Route path="/admin/referral-payouts" element={<AuthGuard allowedRoles={['admin']}><AdminReferralPayouts /></AuthGuard>} />
            <Route path="/admin/referral-applications" element={<AuthGuard allowedRoles={['admin']}><AdminReferralApplications /></AuthGuard>} />
            <Route path="/admin/courses/:courseId/modules" element={<AuthGuard allowedRoles={['admin']}><CourseModules /></AuthGuard>} />
            <Route path="/admin/courses/:courseId/faq" element={<AuthGuard allowedRoles={['admin']}><CourseFaqManager /></AuthGuard>} />
            <Route path="/admin/whatsapp" element={<AuthGuard allowedRoles={['admin']}><WhatsAppManager /></AuthGuard>} />
            <Route path="/admin/email" element={<AuthGuard allowedRoles={['admin']}><EmailManager /></AuthGuard>} />

            {/* Trainer routes */}
            <Route path="/trainer" element={<AuthGuard allowedRoles={['trainer', 'admin']}><TrainerDashboard /></AuthGuard>} />
            <Route path="/trainer/courses" element={<AuthGuard allowedRoles={['trainer', 'admin']}><TrainerCourses /></AuthGuard>} />
            <Route path="/trainer/students" element={<AuthGuard allowedRoles={['trainer', 'admin']}><TrainerStudents /></AuthGuard>} />
            <Route path="/trainer/materials" element={<AuthGuard allowedRoles={['trainer', 'admin']}><TrainerMaterials /></AuthGuard>} />
            <Route path="/trainer/announcements" element={<AuthGuard allowedRoles={['trainer', 'admin']}><TrainerAnnouncements /></AuthGuard>} />
            <Route path="/trainer/earnings" element={<AuthGuard allowedRoles={['trainer', 'admin']}><TrainerEarnings /></AuthGuard>} />

            {/* Dev tools */}
            <Route path="/dev/integration-test" element={<IntegrationTestScreen />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>
      </main>
    </div>
  );
}
